/**
 * iter150AZ.83 — Custom dialogs for the Tests tab.
 *
 * Replaces the browser-native ``window.prompt`` calls that previously
 * gated the Start/Complete actions on fill & loss tests. Browser
 * dialogs are ugly, modal-blocking, and impossible to style for
 * branding — the user explicitly requested "never use the browser
 * built-in dialogs".
 *
 * Two exports:
 *   • ``<TestStartDialog>`` — pick a device (member) from a list-style
 *     radio selector to start a fill-test or loss-test.
 *   • ``<EndPressureDialog>`` — numeric input for the end-pressure
 *     value when completing a test.
 */
import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { CheckCircle, Drop, Flask, CircleNotch } from "@phosphor-icons/react";


export function TestStartDialog({
  open,
  onOpenChange,
  kind,                                   // "fill" | "loss"
  members,                                // [{ device_id, serial_number, ... }]
  /** Only allowed for "fill" — loss tests run group-wide so the
   *  device picker is hidden. Default true (for fill). */
  requireDevice = true,
  onConfirm,                              // async (deviceId) => void
}) {
  const list = Array.isArray(members) ? members : [];
  const [deviceId, setDeviceId] = useState(() => list[0]?.device_id || "");
  const [busy, setBusy] = useState(false);

  // Reset selection whenever the dialog re-opens (so a previous
  // confirm doesn't show as the default on the next round). State
  // writes are deferred via a microtask so the lint rule
  // ``react-hooks/set-state-in-effect`` is satisfied.
  useEffect(() => {
    if (!open) return undefined;
    const handle = setTimeout(() => {
      setDeviceId(list[0]?.device_id || "");
      setBusy(false);
    }, 0);
    return () => clearTimeout(handle);
  }, [open, list]);

  const Icon = kind === "fill" ? Drop : Flask;
  const verb = kind === "fill" ? "fill" : "loss";

  async function handleConfirm() {
    if (requireDevice && !deviceId) return;
    setBusy(true);
    try {
      await onConfirm(deviceId);
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid={`seq-${kind}-start-dialog`}>
        <DialogHeader>
          <DialogTitle className="text-base font-bold flex items-center gap-2">
            <Icon size={18} weight="fill" className={kind === "fill" ? "text-blue-700" : "text-violet-700"} />
            Start {verb} test
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-600">
            {requireDevice
              ? "Choose which compressor you want to run this test on. The test starts immediately and waits for you to record the end pressure."
              : "This test runs across the entire group with every compressor off. Confirm to start the 10-minute leak window."}
          </DialogDescription>
        </DialogHeader>

        {requireDevice && (
          <div className="space-y-1 max-h-72 overflow-y-auto" data-testid={`seq-${kind}-start-device-list`}>
            {list.length === 0 ? (
              <div className="text-xs text-slate-500 italic px-2 py-1.5">
                No members in this group. Add members on the Members tab first.
              </div>
            ) : (
              list.map((m) => {
                const checked = m.device_id === deviceId;
                const label   = m.serial_number || m.device_id;
                return (
                  <label key={m.device_id}
                         data-testid={`seq-${kind}-start-device-${m.device_id}`}
                         data-selected={checked ? "1" : "0"}
                         className={`flex items-center gap-3 px-3 py-2 rounded-sm border cursor-pointer text-xs ${
                           checked
                             ? "border-blue-600 bg-blue-50 ring-1 ring-blue-200"
                             : "border-slate-200 bg-white hover:bg-slate-50"
                         }`}>
                    <input type="radio"
                           name={`seq-${kind}-device`}
                           value={m.device_id}
                           checked={checked}
                           onChange={() => setDeviceId(m.device_id)}
                           className="w-3.5 h-3.5 accent-blue-700"
                           data-testid={`seq-${kind}-start-device-input-${m.device_id}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono font-bold text-slate-900">{label}</div>
                      {m.serial_number && m.serial_number !== m.device_id && (
                        <div className="text-[10px] text-slate-500 font-mono truncate">{m.device_id}</div>
                      )}
                    </div>
                    {checked && <CheckCircle size={14} weight="fill" className="text-blue-600" />}
                  </label>
                );
              })
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <button type="button"
                  onClick={() => onOpenChange(false)}
                  disabled={busy}
                  data-testid={`seq-${kind}-start-cancel`}
                  className="text-xs px-3 py-1.5 rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            Cancel
          </button>
          <button type="button"
                  onClick={handleConfirm}
                  disabled={busy || (requireDevice && !deviceId)}
                  data-testid={`seq-${kind}-start-confirm`}
                  className="text-xs font-bold px-3 py-1.5 rounded-sm bg-blue-700 text-white inline-flex items-center gap-1.5 hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed">
            {busy
              ? <><CircleNotch size={12} weight="bold" className="animate-spin" /> Starting…</>
              : <>Start {verb} test</>}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export function EndPressureDialog({
  open,
  onOpenChange,
  kind,                                   // "fill" | "loss"
  defaultValue = 8.0,
  onConfirm,                              // async (bar) => void
}) {
  const [value, setValue] = useState(String(defaultValue));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    const handle = setTimeout(() => {
      setValue(String(defaultValue));
      setError("");
      setBusy(false);
    }, 0);
    return () => clearTimeout(handle);
  }, [open, defaultValue]);

  async function handleConfirm() {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      setError("Enter a numeric pressure value in bar.");
      return;
    }
    if (parsed < 0 || parsed > 25) {
      setError("Pressure must be between 0 and 25 bar.");
      return;
    }
    setBusy(true);
    try {
      await onConfirm(parsed);
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" data-testid={`seq-${kind}-end-dialog`}>
        <DialogHeader>
          <DialogTitle className="text-base font-bold">
            Record end pressure
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-600">
            {kind === "fill"
              ? "Pressure observed on the discharge gauge at the moment the compressor cut out."
              : "Pressure observed at the end of the 10-minute leak window (all compressors off)."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
            End pressure (bar)
          </label>
          <div className="flex items-stretch gap-0">
            <input type="number"
                   inputMode="decimal"
                   step="0.1"
                   value={value}
                   onChange={(e) => { setValue(e.target.value); setError(""); }}
                   onKeyDown={(e) => { if (e.key === "Enter") handleConfirm(); }}
                   autoFocus
                   data-testid={`seq-${kind}-end-input`}
                   className={`flex-1 text-base px-3 py-2 border rounded-l-sm font-mono ${
                     error ? "border-rose-400 ring-1 ring-rose-200" : "border-slate-300"
                   }`} />
            <span className="inline-flex items-center px-3 text-xs font-bold uppercase tracking-wider text-slate-500 border border-l-0 border-slate-300 bg-slate-50 rounded-r-sm">
              bar
            </span>
          </div>
          {error && (
            <div className="text-[11px] text-rose-700 mt-1" data-testid={`seq-${kind}-end-error`}>
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <button type="button"
                  onClick={() => onOpenChange(false)}
                  disabled={busy}
                  data-testid={`seq-${kind}-end-cancel`}
                  className="text-xs px-3 py-1.5 rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            Cancel
          </button>
          <button type="button"
                  onClick={handleConfirm}
                  disabled={busy}
                  data-testid={`seq-${kind}-end-confirm`}
                  className="text-xs font-bold px-3 py-1.5 rounded-sm bg-emerald-700 text-white inline-flex items-center gap-1.5 hover:bg-emerald-800 disabled:opacity-50">
            {busy
              ? <><CircleNotch size={12} weight="bold" className="animate-spin" /> Saving…</>
              : <>Save end pressure</>}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
